import React, { useEffect, useMemo, useState } from 'react';
import { User, AccountType } from '../types';
import { db } from '../firebase';
import { collection, deleteField, doc, getDocs, updateDoc } from 'firebase/firestore';
import { logChange } from '../lib/changeLog';
import { Icons } from '../constants';
import { Search, ShieldCheck, UserCog } from 'lucide-react';

interface Props {
  currentUser: User;
  actingUser?: string;
}

interface RoleOption {
  id: AccountType;
  label: string;
  blurb: string;
  role: User['role'];
  staffRole?: User['staffRole'];
}

const ROLE_OPTIONS: RoleOption[] = [
  { id: 'client',     label: 'Client',     blurb: 'Customer portal',        role: 'client' },
  { id: 'friend',     label: 'Friend',     blurb: 'Community / referral',   role: 'client' },
  { id: 'applicant',  label: 'Applicant',  blurb: 'Application pending',    role: 'applicant' },
  { id: 'contractor', label: 'Contractor', blurb: 'Crew / contract climber', role: 'staff', staffRole: 'contractor' },
  { id: 'employee',   label: 'Employee',   blurb: 'Crew workspace',         role: 'staff', staffRole: 'employee' },
  { id: 'admin',      label: 'Admin',      blurb: 'Full management access', role: 'staff', staffRole: 'admin' },
];

const labelFor = (u: User): AccountType => {
  if (u.accountType && ROLE_OPTIONS.some((r) => r.id === u.accountType)) return u.accountType;
  if (u.role === 'staff') return (u.staffRole as AccountType) || 'employee';
  if (u.role === 'applicant') return 'applicant';
  return 'client';
};

const AccessControlView: React.FC<Props> = ({ currentUser, actingUser }) => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [busyId, setBusyId] = useState('');
  const [savedId, setSavedId] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const snap = await getDocs(collection(db, 'users'));
      const list = snap.docs
        .map((d) => ({ id: d.id, ...d.data() } as User))
        .filter((u) => !!u.email)
        .sort((a, b) => (a.name || a.email).localeCompare(b.name || b.email));
      setUsers(list);
    } catch (e: any) {
      setError(e?.code === 'permission-denied'
        ? 'Only an admin can load the user list. Ask the owner to check your role.'
        : (e?.message || 'Could not load users.'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const counts = useMemo(() => {
    const c: Record<string, number> = {};
    for (const u of users) c[labelFor(u)] = (c[labelFor(u)] || 0) + 1;
    return c;
  }, [users]);

  const filtered = users.filter((u) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return [u.name, u.email, labelFor(u)].some((v) => (v || '').toLowerCase().includes(q));
  });

  const changeRole = async (u: User, next: AccountType) => {
    if (u.id === currentUser.id) {
      alert('You cannot change your own access level. Ask another admin to do it.');
      return;
    }
    if (next === 'employee' || next === 'admin') {
      const ok = window.confirm(
        `Give ${u.name || u.email} ${next === 'admin' ? 'full admin' : 'employee'} access? ` +
        `They will see the staff workspace on their next login.`
      );
      if (!ok) return;
    }
    const option = ROLE_OPTIONS.find((r) => r.id === next);
    if (!option) return;
    setBusyId(u.id);
    setError('');
    try {
      await updateDoc(doc(db, 'users', u.id), {
        role: option.role,
        staffRole: option.staffRole ? option.staffRole : deleteField(),
        accountType: next,
        accessUpdatedBy: actingUser || currentUser.name || currentUser.email,
        accessUpdatedAt: new Date().toISOString(),
      });
      setUsers((prev) => prev.map((x) => x.id === u.id
        ? { ...x, role: option.role, staffRole: option.staffRole, accountType: next }
        : x));
      setSavedId(u.id);
      setTimeout(() => setSavedId(''), 2500);
      logChange({
        who: actingUser || currentUser.name || 'Admin',
        whoRole: 'admin',
        action: 'UPDATE',
        entity: 'user access',
        entityId: u.id,
        entityLabel: u.name || u.email,
        summary: `Access changed to ${option.label} for ${u.name || u.email}`,
        changedFields: ['role', 'staffRole', 'accountType'],
      });
    } catch (e: any) {
      setError(e?.code === 'permission-denied'
        ? 'The update was blocked by Firestore rules. Make sure you are signed in as an admin.'
        : (e?.message || 'Could not update access.'));
    } finally {
      setBusyId('');
    }
  };

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-2xl font-black text-bark flex items-center gap-2">
            <UserCog size={24} className="text-palm" /> Users &amp; Access
          </h2>
          <p className="text-stone-500 text-sm mt-1">
            Only admins can grant employee or admin access. New sign-ups choose Client, Contractor, Friend or Applicant.
          </p>
        </div>
        <div className="flex gap-2 text-[10px] font-black uppercase tracking-wider">
          <span className="px-3 py-1.5 rounded-full bg-amber-50 text-amber-700">Applicants {counts.applicant || 0}</span>
          <span className="px-3 py-1.5 rounded-full bg-gecko/10 text-palm">Employees {counts.employee || 0}</span>
          <span className="px-3 py-1.5 rounded-full bg-violet-50 text-violet-700">Admins {counts.admin || 0}</span>
        </div>
      </div>

      <div className="relative max-w-sm">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search name, email or access level..."
          className="w-full pl-9 pr-4 py-2.5 bg-white border border-stone-200 rounded-xl text-sm font-bold text-bark focus:ring-2 focus:ring-gecko outline-none"
        />
      </div>

      {error && (
        <div className="p-3.5 bg-red-50 border border-red-200 rounded-2xl text-red-700 text-xs font-bold">
          {error}
        </div>
      )}

      {loading ? (
        <p className="text-sm text-stone-400 font-bold">Loading users…</p>
      ) : (
        <div className="space-y-2">
          {filtered.map((u) => {
            const current = labelFor(u);
            const isSelf = u.id === currentUser.id;
            return (
              <div key={u.id} className="bg-white rounded-2xl border border-stone-100 shadow-sm p-4 flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-black text-bark truncate">{u.name || '(no name)'}</p>
                    {isSelf && <span className="text-[9px] font-black uppercase tracking-wider bg-stone-100 text-stone-500 px-2 py-0.5 rounded-full">You</span>}
                    {current === 'admin' && <ShieldCheck size={13} className="text-violet-500" />}
                  </div>
                  <p className="text-xs text-stone-400 truncate">{u.email}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className={`text-[10px] font-black uppercase tracking-wider px-2.5 py-1 rounded-full ${
                    current === 'admin' ? 'bg-violet-100 text-violet-700'
                    : current === 'employee' ? 'bg-gecko/10 text-palm'
                    : current === 'contractor' ? 'bg-sky-100 text-sky-700'
                    : current === 'applicant' ? 'bg-amber-100 text-amber-700'
                    : current === 'friend' ? 'bg-rose-50 text-rose-600'
                    : 'bg-stone-100 text-stone-600'
                  }`}>
                    {ROLE_OPTIONS.find((r) => r.id === current)?.label || current}
                  </span>
                  <select
                    value={current}
                    disabled={isSelf || busyId === u.id}
                    onChange={(e) => changeRole(u, e.target.value as AccountType)}
                    className="bg-stone-50 border border-stone-200 rounded-xl px-3 py-2 text-xs font-bold text-bark focus:ring-2 focus:ring-gecko outline-none disabled:opacity-50"
                    title={isSelf ? 'Ask another admin to change your access' : 'Change access level'}
                  >
                    {ROLE_OPTIONS.map((r) => (
                      <option key={r.id} value={r.id}>{r.label} — {r.blurb}</option>
                    ))}
                  </select>
                  {savedId === u.id && <span className="text-[10px] font-black uppercase text-gecko">Saved</span>}
                </div>
              </div>
            );
          })}
          {filtered.length === 0 && <p className="text-sm text-stone-400 font-bold">No matching users.</p>}
        </div>
      )}

      <div className="bg-stone-50 border border-stone-100 rounded-2xl p-4 text-xs text-stone-500 leading-relaxed">
        <p className="font-black text-stone-600 uppercase tracking-wider text-[10px] mb-1">How new accounts work</p>
        New sign-ups choose Client, Contractor, Friend or Applicant. Employee and Admin are only available here, to an
        existing admin. Changing an applicant to Employee or Contractor upgrades them on their next login — they keep the
        same account and password.
      </div>
    </div>
  );
};

export default AccessControlView;
